/**
 * Mythium Cutline Mask — concave-hull-at-scale-α
 *
 * Produces a binary alpha-only PNG that the SVG renderer feeds into its
 * cutline filter (instead of using the image's own alpha). Algorithm:
 *
 *   1. Binary opaque map from the source alpha.
 *   2. Morphological CLOSING (dilate α then erode α). This is the pixel-
 *      based approximation of an α-shape / concave hull: features within
 *      2α of each other are bridged into a single silhouette, anything
 *      farther stays separate.
 *   3. 4-connected components on the closed map; keep the LARGEST. Loose
 *      blobs that are too far from the figure to bridge during closing
 *      become smaller components and are dropped.
 *   4. Edge-BFS on the largest-component map: anything not reachable from
 *      the canvas edge is an interior hole and gets folded into the mask.
 *
 * One dialable parameter, ALPHA_RADIUS_PX, controls everything:
 *   - Bridges a 2·α-wide gap between figure features (sword ↔ body,
 *     dwarf-staff ↔ loose specks).
 *   - Closes a 2·α-wide leg-gap at the bottom (no plinth needed).
 *   - Anything farther than 2·α from the figure stays a separate
 *     component and gets dropped.
 *
 * The exported mask is the figure's outer silhouette with all interior
 * holes filled. The renderer uses it as the source for feMorphology.dilate,
 * so the cutline traces only the outer contour. The visible figure (with
 * its original holes) draws on top so interior transparent regions show
 * through as clear acrylic.
 */

(function () {
  'use strict';

  // Only truly clear pixels (α = 0) count as transparent.
  const ALPHA_THRESHOLD = 1;

  // Morphological closing radius. Pipeline:
  //   1. Dilate the figure-alpha by CLOSING_RADIUS_PX + SILHOUETTE_MARGIN_PX
  //      → nearby thin protrusions (dagger, blade, dress flap, hair tips)
  //      bridge into one continuous silhouette together with the body.
  //   2. Connected components on the dilated map; keep the largest only.
  //      Distant noise specks stay smaller-than-largest components and
  //      get dropped.
  //   3. Erode by CLOSING_RADIUS_PX (NOT the full dilation) to restore
  //      the silhouette thickness — leaves SILHOUETTE_MARGIN_PX of extra
  //      outward growth everywhere, so thin features sit inside a wider
  //      mask boundary and the rim band's clear-acryl buffer reads with
  //      the same visual width around them as around the body.
  // The cutline-band is then built on this slightly-expanded shape.
  const CLOSING_RADIUS_PX = 18;
  // Extra outward expansion of the mask silhouette after the closing.
  // = (dilation amount − erosion amount). Adds this many pixels of mask
  // thickness everywhere so the band's transparent buffer reads with the
  // same width around dagger / blade / foot as around torso / head, and
  // also gives the figure plenty of "lucht" before the outer rim.
  const SILHOUETTE_MARGIN_PX = 10;
  // Padding (in pixels) added around the input image internally before
  // running dilation/erosion. The figure's silhouette often touches the
  // cropped image's edges (head top, foot bottom, staff/lantaarn left),
  // and without padding the dilations get clipped flat against those
  // edges — visible as a rectangular cutline. We pad here instead of
  // upstream so downstream code (rim-color, renderer) keeps receiving
  // PNGs at the SAME dimensions as the input: the padded working canvas
  // is cropped back to (W, H) before the mask blob is emitted.
  const INTERNAL_PAD_PX = 120;
  // Gaussian blur radius (px) applied to the final binary mask, followed
  // by an alpha > 128 re-threshold. Egaliseert de blokkerige hoeken die
  // de 4-conn dilation oplevert tot soepele rondingen. The threshold
  // sits at the midpoint of the blur falloff so the silhouette boundary
  // stays put on average — only the corners get smoothed. 0 = uit.
  const SMOOTH_BLUR_PX = 10;
  // Small additional closing radius applied AFTER the main hole-fill.
  // Fuses figure features whose rim strokes would otherwise touch
  // (bijl-blade ↔ been): a small dilation of the silhouette encloses
  // the narrow gap between two near-touching features; pixels enclosed
  // by that dilation but outside the silhouette are added to the
  // silhouette, so the rim runs as one continuous contour around the
  // merged region. Only the enclosed pocket is added — the dilation
  // ring around the silhouette is NOT, so the outer contour at
  // ungekoppelde features stays where it was.
  const GAP_CLOSE_PX = 6;

  function loadImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = url;
    });
  }

  function canvasToBlobUrl(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((b) => {
        if (!b) return reject(new Error('toBlob failed'));
        resolve(URL.createObjectURL(b));
      }, 'image/png');
    });
  }

  /** Yield to the browser so the UI gets to repaint. */
  const yieldToMain = () => new Promise(r => setTimeout(r, 0));

  /** Iterative 4-conn dilation. Yields between passes so a 25-pass run
   *  doesn't freeze the main thread for ~400 ms. */
  async function dilate(buf, W, H, passes) {
    let cur = buf;
    for (let p = 0; p < passes; p++) {
      const next = new Uint8Array(cur);
      for (let y = 0; y < H; y++) {
        const row = y * W;
        for (let x = 0; x < W; x++) {
          const idx = row + x;
          if (cur[idx]) continue;
          if (
            (x > 0     && cur[idx - 1]) ||
            (x < W - 1 && cur[idx + 1]) ||
            (y > 0     && cur[idx - W]) ||
            (y < H - 1 && cur[idx + W])
          ) next[idx] = 1;
        }
      }
      cur = next;
      if (p < passes - 1) await yieldToMain();
    }
    return cur;
  }

  /** Iterative 4-conn erosion: opaque pixel becomes 0 if any 4-neighbour
   *  is 0 (or off-canvas). Yields between passes. */
  async function erode(buf, W, H, passes) {
    let cur = buf;
    for (let p = 0; p < passes; p++) {
      const next = new Uint8Array(cur);
      for (let y = 0; y < H; y++) {
        const row = y * W;
        for (let x = 0; x < W; x++) {
          const idx = row + x;
          if (!cur[idx]) continue;
          if (
            x === 0     || !cur[idx - 1] ||
            x === W - 1 || !cur[idx + 1] ||
            y === 0     || !cur[idx - W] ||
            y === H - 1 || !cur[idx + W]
          ) next[idx] = 0;
        }
      }
      cur = next;
      if (p < passes - 1) await yieldToMain();
    }
    return cur;
  }

  /**
   * Build the cutline mask from a PNG URL. Returns a new blob URL for
   * the mask, or null if the image has no usable alpha.
   */
  async function buildMask(imageUrl) {
    if (!imageUrl) return null;

    let img;
    try { img = await loadImage(imageUrl); } catch (e) { return null; }
    const origW = img.naturalWidth, origH = img.naturalHeight;
    if (!origW || !origH) return null;

    // Internal padded canvas: figure pixels sit at (PAD, PAD)..(PAD+origW, PAD+origH).
    // All morphology runs in this padded space so the figure never
    // touches the working canvas's edge. The mask is cropped back to
    // (origW, origH) before being written to the output blob.
    const PAD = INTERNAL_PAD_PX;
    const W = origW + 2 * PAD;
    const H = origH + 2 * PAD;

    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, PAD, PAD);

    let imgData;
    try { imgData = ctx.getImageData(0, 0, W, H); } catch (e) { return null; }
    const srcAlpha = imgData.data;
    const N = W * H;

    // 1. Binary opaque map.
    const opaque = new Uint8Array(N);
    let anyOpaque = false;
    for (let i = 0; i < N; i++) {
      if (srcAlpha[i * 4 + 3] >= ALPHA_THRESHOLD) { opaque[i] = 1; anyOpaque = true; }
    }
    if (!anyOpaque) return null;

    // 2. Dilate the figure by CLOSING_RADIUS_PX + SILHOUETTE_MARGIN_PX
    //    so nearby thin features bridge to the body (and any < 2·R-wide
    //    leg gaps fill in). Erode in step 4 will only undo CLOSING_RADIUS_PX
    //    of that, leaving SILHOUETTE_MARGIN_PX of extra mask thickness.
    const dilated = await dilate(opaque, W, H, CLOSING_RADIUS_PX + SILHOUETTE_MARGIN_PX);

    // 3. Connected components on the DILATED map (not on the closed
    //    map). Components track which features are merged into the
    //    body via the dilation bridges. Keep only the largest one;
    //    distant noise specks become smaller components and disappear.
    const compId = new Int32Array(N).fill(-1);
    const sizes = [];
    const queue = new Int32Array(N);
    let bfsCounter = 0;
    for (let start = 0; start < N; start++) {
      if (!dilated[start] || compId[start] !== -1) continue;
      const id = sizes.length;
      let qh = 0, qt = 0;
      queue[qt++] = start;
      compId[start] = id;
      let size = 0;
      while (qh < qt) {
        const idx = queue[qh++];
        size++;
        bfsCounter++;
        if ((bfsCounter & 0xFFFF) === 0) await yieldToMain(); // every ~65k ops
        const x = idx % W;
        const y = (idx - x) / W;
        const tryN = (n) => {
          if (compId[n] === -1 && dilated[n]) {
            compId[n] = id;
            queue[qt++] = n;
          }
        };
        if (x > 0)     tryN(idx - 1);
        if (x < W - 1) tryN(idx + 1);
        if (y > 0)     tryN(idx - W);
        if (y < H - 1) tryN(idx + W);
      }
      sizes.push(size);
    }
    if (sizes.length === 0) return null;
    let largestId = 0;
    for (let i = 1; i < sizes.length; i++) {
      if (sizes[i] > sizes[largestId]) largestId = i;
    }
    const inLargestDilated = new Uint8Array(N);
    for (let i = 0; i < N; i++) {
      inLargestDilated[i] = compId[i] === largestId ? 1 : 0;
    }

    // 4. Erode the largest-component-of-dilated by CLOSING_RADIUS_PX to
    //    restore the original silhouette thickness for the features we
    //    just kept. Equivalent to closing + largest, but in this order
    //    we never run components on the closed map (cheaper) and the
    //    dropped components are guaranteed truly disconnected.
    const inLargest = await erode(inLargestDilated, W, H, CLOSING_RADIUS_PX);

    // 4. Edge-BFS on the inLargest=0 pixels: anything reachable from the
    //    canvas edge through non-component pixels is exterior; the rest
    //    is enclosed by the component (interior holes that survived the
    //    closing — closes them too).
    const reached = new Uint8Array(N);
    let qh = 0, qt = 0;
    const seed = (idx) => {
      if (!reached[idx] && !inLargest[idx]) {
        reached[idx] = 1;
        queue[qt++] = idx;
      }
    };
    for (let x = 0; x < W; x++) { seed(x); seed((H - 1) * W + x); }
    for (let y = 1; y < H - 1; y++) { seed(y * W); seed(y * W + (W - 1)); }
    let edgeBfsCounter = 0;
    while (qh < qt) {
      const idx = queue[qh++];
      edgeBfsCounter++;
      if ((edgeBfsCounter & 0xFFFF) === 0) await yieldToMain();
      const x = idx % W;
      const y = (idx - x) / W;
      if (x > 0)     seed(idx - 1);
      if (x < W - 1) seed(idx + 1);
      if (y > 0)     seed(idx - W);
      if (y < H - 1) seed(idx + W);
    }

    // 5b. Build the filled silhouette = inLargest with interior holes
    //     folded in via the edge-BFS reached map.
    const filled = new Uint8Array(N);
    for (let i = 0; i < N; i++) {
      filled[i] = (inLargest[i] || !reached[i]) ? 1 : 0;
    }

    // 5c. Gap-close pocket-fill. Dilate the filled silhouette by a small
    //     radius; anything outside the silhouette that becomes enclosed
    //     by that dilation is a narrow gap between features whose rim
    //     strokes would touch — fold those pockets into the silhouette.
    //     Pixels in the dilation expansion ring (within GAP_CLOSE_PX of
    //     the silhouette but not enclosed) are NOT added, so the outer
    //     contour at solitaire features stays put.
    if (GAP_CLOSE_PX > 0) {
      const gapDilated = await dilate(filled, W, H, GAP_CLOSE_PX);
      const reachedGap = new Uint8Array(N);
      qh = 0; qt = 0;
      const seedGap = (idx) => {
        if (!reachedGap[idx] && !gapDilated[idx]) {
          reachedGap[idx] = 1;
          queue[qt++] = idx;
        }
      };
      for (let x = 0; x < W; x++) { seedGap(x); seedGap((H - 1) * W + x); }
      for (let y = 1; y < H - 1; y++) { seedGap(y * W); seedGap(y * W + (W - 1)); }
      let gapBfsCounter = 0;
      while (qh < qt) {
        const idx = queue[qh++];
        gapBfsCounter++;
        if ((gapBfsCounter & 0xFFFF) === 0) await yieldToMain();
        const x = idx % W;
        const y = (idx - x) / W;
        if (x > 0)     seedGap(idx - 1);
        if (x < W - 1) seedGap(idx + 1);
        if (y > 0)     seedGap(idx - W);
        if (y < H - 1) seedGap(idx + W);
      }
      for (let i = 0; i < N; i++) {
        if (!gapDilated[i] && !reachedGap[i]) filled[i] = 1;
      }
    }

    // 6. Write the binary alpha mask onto the padded canvas.
    const outImg = ctx.createImageData(W, H);
    for (let i = 0; i < N; i++) {
      outImg.data[i * 4 + 3] = filled[i] ? 255 : 0;
    }
    ctx.putImageData(outImg, 0, 0);

    // 7. Smoothing pass: Gaussian blur + re-threshold. Vlakt de
    //    blokkerige stempels van de 4-conn dilation af tot soepele
    //    rondingen. The > 128 threshold sits at the midpoint of the
    //    blur falloff, so the silhouette grens netto on dezelfde plek
    //    blijft — alleen hoeken worden gladder. Done on the padded
    //    canvas so the blur has room around the silhouette.
    if (SMOOTH_BLUR_PX > 0) {
      const blurCanvas = document.createElement('canvas');
      blurCanvas.width = W; blurCanvas.height = H;
      const blurCtx = blurCanvas.getContext('2d', { willReadFrequently: true });
      if (blurCtx) {
        blurCtx.filter = `blur(${SMOOTH_BLUR_PX}px)`;
        blurCtx.drawImage(canvas, 0, 0);
        let blurredData;
        try { blurredData = blurCtx.getImageData(0, 0, W, H); } catch (e) { blurredData = null; }
        if (blurredData) {
          const bd = blurredData.data;
          for (let i = 0; i < N; i++) {
            bd[i * 4 + 3] = bd[i * 4 + 3] > 128 ? 255 : 0;
          }
          ctx.putImageData(blurredData, 0, 0);
        }
      }
    }

    // 8. Crop back to (origW, origH) so downstream consumers (rim-color,
    //    the renderer) receive a PNG with the same dimensions as the
    //    input.
    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = origW;
    cropCanvas.height = origH;
    const cropCtx = cropCanvas.getContext('2d');
    if (!cropCtx) return null;
    cropCtx.drawImage(canvas, PAD, PAD, origW, origH, 0, 0, origW, origH);

    try { return await canvasToBlobUrl(cropCanvas); } catch (e) { return null; }
  }

  window.MythiumCutlineMask = { buildMask: buildMask };
})();
